import { AttendanceGuage, Button } from '@wot/components';
import { ISession, ITrialClass, ITutor } from '@wot/typings';
import { compareAsc } from 'date-fns';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { plansApi } from '../../api/plan';
import { sessionsApi } from '../../api/sessions';
import tutorsApi from '../../api/tutors';
import { DEFAULT_PROFILE_PIC } from '../../assets/icons';
import Footer from '../../components/Landing/Footer';
import ProfileCard from '../../components/ProfileCard';
import Slider from '../../components/Slider';
import TutorDiv from '../../components/TutorDiv';
import { useAppSelector } from '../../hooks';
import { LPlan } from '../../interfaces/Plan';
import { getAttendacePercentage } from '../../utils/functions';
import { ClassItem } from '../MyCalender';
import { SEO } from '../../components/SEO';

export default function Home() {
  const { userData } = useAppSelector((state) => state.userSlice);
  const [allTutors, setAllTutors] = useState<ITutor[]>([]);
  const [recentlyViewedTutors, setrecentlyViewedTutors] = useState<ITutor[]>([]);
  const [totalSessions, setTotalSessions] = useState<ITrialClass[]>([]);
  const [nextSession, setNextSession] = useState<ISession>();
  const [plans, setPlans] = useState<LPlan[]>([]);
  const [activePlan, setActivePlan] = useState<LPlan>();

  useEffect(() => {
    tutorsApi.getTutors().then((data) => {
      setAllTutors(data.filter((t: any) => t.status === "active"));
    });
    if (userData) {
      tutorsApi.getRecentlyViewedTutors(userData._id).then((data) => {
        if (data[0]) {
          setrecentlyViewedTutors(data[0].recentlyViewed.filter((s: any) => s.status === "active").slice(-4).reverse());
        } //set only last 4 elemets of the array
      });
    }
  }, [userData]);

  useEffect(() => {
    if (userData?._id) {
      plansApi.get(userData?._id).then((data) => {
        setPlans(data);
      });
    }
  }, []);

  useEffect(() => {
    if (plans) {
      let temp = JSON.parse(JSON.stringify(plans));
      let sortedPlans = temp.sort((a: any, b: any) => compareAsc(new Date(a.createdAt), new Date(b.createdAt)));
      let active = sortedPlans.find((item: any) => item.status === 'active');
      if (active) {
        setActivePlan(active);
      }
    }
  }, [plans]);

  useEffect(() => {
    if (userData?._id) {
      sessionsApi.get(userData._id).then((data) => {
        let sortedSlots = data.sort((a: any, b: any) =>
          compareAsc(new Date(a.scheduledTime.start), new Date(b.scheduledTime.start))
        );
        let next = sortedSlots.find((item: any) => item.status === 'upcoming');
        setNextSession(next);
      });
    }
  }, []);

  useEffect(() => {
    if (userData?._id) {
      sessionsApi.get(userData?._id, { type: 'plan', status: 'completed' }).then((data) => {
        setTotalSessions(data);
      });
    }
  }, []);

  const Cards = () => {
    return (
      <>
        <ProfileCard
          title="My Attendance"
          link="/attendance"
          content={
            <>
              {totalSessions ? (
                <div className="w-full  max-h-[256px] flex flex-col items-center bg-white rounded-xl p-4">
                  <div className="w-[50%] md:w-[50%] text-2xl grow flex items-center justify-center p-4">
                    <AttendanceGuage
                      percentage={getAttendacePercentage(
                        totalSessions.filter((i) => i.attendance && i.attendance.student === 'present').length,
                        totalSessions.length
                      )}
                    />
                  </div>
                </div>
              ) : null}
            </>
          }
          placeHolder={
            <p className="font-medium">Your attendance stats will show up here once you are enrolled for any class</p>
          }
        />
        <ProfileCard
          title={`Active Plans (${plans.filter((plan) => plan.status === 'active').length})`}
          link="/my-plans"
          content={
            activePlan ? (
              <div className="w-full flex flex-col gap-2 border border-l-4 border-primary rounded-2xl p-4">
                <p className="text-xl font-semibold">{activePlan.tutor.name}</p>
                <p className="text-sm text-neutralGray2 font-medium">
                  {activePlan.totalSessions} Classes / 1hr Classes
                </p>
                <div className="w-full flex flex-col gap-1 py-4 border-t">
                  <div className="w-full flex justify-between">
                    <p className="text-sm text-black/80">Scheduled Lesssons</p>
                    <p className="text-sm text-black/80">
                      {activePlan.sessions?.length || 0}/{activePlan.totalSessions}
                    </p>
                  </div>
                  <div className="relative w-full h-2 bg-neutralBlue1 rounded-full">
                    <div
                      style={{
                        width: `${activePlan.sessions ? (activePlan.sessions?.length / activePlan.totalSessions) * 100 : 0
                          }%`
                      }}
                      className="absolute bg-primary w-full h-full top-0 left-0 rounded-full"
                    ></div>
                  </div>
                </div>
              </div>
            ) : null
          }
          placeHolder={
            <div className="w-full h-full flex flex-col gap-5 items-center">
              <p className="text-sm">
                You are not enrolled with any tutor, schedule classes with a tutor to view your classes for today
              </p>
              <div className="mt-auto">
                <Link to="/find-tutor">
                  <Button text="Find a tutor" onClick={() => { }} />
                </Link>
              </div>
            </div>
          }
        />
        <ProfileCard
          title="Next Session"
          link="/my-calender"
          content={nextSession ? <ClassItem session={nextSession} /> : null}
          placeHolder={
            <div className="w-full h-full flex flex-col gap-5 items-center">
              <p className="text-sm">
                You are not enrolled with any tutor, schedule classes with a tutor to view your classes for today
              </p>
              <div className="mt-auto">
                <Link to="/find-tutor">
                  <Button text="Find a tutor" onClick={() => { }} />
                </Link>
              </div>
            </div>
          }
        />
      </>
    );
  };

  return (
    <>
    <SEO 
      title="Dashboard - Manage Your Learning"
      description="Access your WorldoTutor dashboard to manage your tutoring sessions, view upcoming classes, check attendance, and explore new tutors."
      keywords="dashboard, my account, tutoring sessions, attendance, learning management"
      url="/dashboard"
      type="website"
      noIndex={true}
      og={{
        type: 'website',
        siteName: 'WorldoTutor',
        locale: 'en_US',
        image: {
          url: '/imgi_1_favicon.png',
          alt: 'Dashboard - WorldoTutor',
          width: 1200,
          height: 630
        }
      }}
      twitter={{
        card: 'summary_large_image',
        site: '@worldotutor',
        image: {
          url: '/imgi_1_favicon.png',
          alt: 'Dashboard - WorldoTutor'
        }
      }}
    />
    <div>
      <div className="bg-[#F5F6F9]">
        <div className="flex flex-col container m-auto py-5 z-0">
          <div>
            <div className="grid lg:grid-cols-3 grid-cols-1 gap-5 mb-5">
              <div className="w-full h-full bg-white col-span-1 flex flex-col items-center rounded-2xl overflow-hidden drop-shadow-md">
                <div className="flex flex-col items-center gap-3 py-4 mb-5">
                  <div className="h-24 w-24 rounded-full overflow-hidden">
                    <img
                      src={userData?.profilePic === '' ? DEFAULT_PROFILE_PIC : userData?.profilePic}
                      alt="User Pic"
                      className="w-full h-full  object-cover"
                    />
                  </div>
                  <div>
                    <span className="text-2xl font-normal">{userData?.name || 'Guest User'}</span>
                  </div>
                </div>
                <div className="flex justify-center px-4 mb-5">
                  {plans.length === 0 && (
                    <p className="text-sm md:text-base font-normal text-center md:w-4/5">
                      You don&apos;t have any plans and tutors. Start searching for a tutor as your requirement and
                      schedule classes.
                    </p>
                  )}
                </div>
                <div className="w-full py-2 px-4 bg-neutralBlue2 flex items-center gap-2 divide-x-2 mt-auto">
                  <div className="w-full flex flex-col items-center px-2">
                    <p className="text-base font-semibold">{plans.filter((plan) => plan.status === 'active').length}</p>
                    <p className="text-sm font-medium text-neutralGray2 text-center whitespace-nowrap">Active Plans</p>
                  </div>
                  <div className="w-full  flex flex-col items-center px-2">
                    <p className="text-base font-semibold">
                      {
                        _.uniqBy(plans, (plan) => {
                          plan.tutor && plan.tutor.name;
                        }).length
                      }
                    </p>
                    <p className="text-sm font-medium text-neutralGray2">Tutors</p>
                  </div>
                  <div className="w-full  flex flex-col items-center px-2">
                    <p className="text-base font-semibold">{_.uniqBy(plans, (plan) => plan.category.name).length}</p>
                    <p className="text-sm font-medium text-neutralGray2">Subjects</p>
                  </div>
                  {/* <Link to="/find-tutor">
                    <Button type='secondary' text="Find a tutor" onClick={() => { }} />
                  </Link> */}
                </div>
              </div>
              <div className="bg-neutralBlue2 lg:col-span-2 col-span-1 rounded-2xl p-8 flex flex-col gap-8 drop-shadow-md">
                <div>
                  <span className="text-2xl font-semibold">Start your journey now</span>
                </div>
                <div className="flex flex-col gap-8 relative">
                  <div className="absolute left-4 border border-neutralGray2 h-full z-0"></div>
                  <div className="flex gap-6 items-center z-10">
                    <div className="bg-primary rounded-full h-8 w-8 flex items-center justify-center shrink-0">
                      <span className="text-sm font-medium text-white">1</span>
                    </div>
                    <span className="text-lg font-medium">
                      Share your requirement with us or find a tutor by your self
                    </span>
                  </div>
                  <div className="flex gap-6 items-center z-10">
                    <div className="bg-primary rounded-full h-8 w-8 flex items-center justify-center shrink-0">
                      <span className="text-sm font-medium text-white">2</span>
                    </div>
                    <span className="text-lg font-medium">Select day and time as per your convenience</span>
                  </div>
                  <div className="flex gap-6 items-center z-10">
                    <div className="bg-primary rounded-full h-8 w-8 flex items-center justify-center shrink-0">
                      <span className="text-sm font-medium text-white">3</span>
                    </div>
                    <span className="text-lg font-medium">Take your trial lesson!</span>
                  </div>
                </div>
                <div className="flex justify-end gap-5">
                  <div>
                    <Link to="/post-requirement">
                      <Button type="s-outlined" text="Post a Requirement" onClick={() => { }} />
                    </Link>
                  </div>
                  <div>
                    <Link to="/find-tutor">
                      <Button text="Find a tutor" onClick={() => { }} />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:grid lg:grid-cols-3 lg:gap-5 lg:justify-between hidden">
              <Cards />
            </div>
            <div className="lg:hidden">
              <Slider>
                <Cards />
              </Slider>
            </div>
          </div>
          <div>
            {recentlyViewedTutors && recentlyViewedTutors.length > 0 && (
              <TutorDiv title="Your Recently Viewed Tutors" button={false} tutors={recentlyViewedTutors} />
            )}
            {/* <TutorDiv
              title="Recommended Tutors"
              button={true}
              tutors={allTutors}
            /> */}
            <TutorDiv title="Explore Tutors" button={true} btnLink={'/find-tutor'} tutors={allTutors} />
            <TutorDiv title="Popular Tutors" button={false} tutors={allTutors} />
          </div>
        </div>
      </div>
      <div className="bg-neutralBlack">
        <Footer />
      </div>
    </div>
    </>
  );
}
